import React, { useState } from "react";
import ReactDOM from "react-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { EstablishmentReportCounts, EstablishmentReportType } from "../../interfaces/establishment";
import { REPORT_LABELS, PAYMENT_REPORT_TYPES } from "../../utils/reportConstants";

/**
 * From this many reports on a single reason, the sign turns red instead of orange.
 * Graded on the worst reason rather than the sum: five users flagging "Impayés" is a
 * different warning from five users flagging five unrelated one-off things.
 */
export const REPORT_ALERT_THRESHOLD = 5;

const TOOLTIP_WIDTH = 224; // w-56

type ReportEntry = [EstablishmentReportType, number];

/**
 * The warning sign on an establishment line: orange while a reason has been flagged a few
 * times, red once one reason reaches REPORT_ALERT_THRESHOLD. Just the sign — every number
 * and every reason lives in the hover tooltip, which lists them all, so an establishment
 * reported for both unpaid invoices and late payment shows both lines there.
 *
 * Renders nothing when nobody reported the establishment.
 */
export default function ReportSignalBadge({ counts }: { counts?: EstablishmentReportCounts }) {
    const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

    const byType = counts?.byType ?? {};

    // Payment reasons first and in a fixed order — they are what a commercial reads before
    // calling — then the rest, worst first.
    const paymentEntries = PAYMENT_REPORT_TYPES
        .map(type => [type, byType[type] ?? 0] as ReportEntry)
        .filter(([, n]) => n > 0);
    const otherEntries = (Object.entries(byType) as ReportEntry[])
        .filter(([type, n]) => n > 0 && !PAYMENT_REPORT_TYPES.includes(type))
        .sort((a, b) => b[1] - a[1]);
    const entries = [...paymentEntries, ...otherEntries];

    if (entries.length === 0) return null;

    const total = entries.reduce((sum, [, n]) => sum + n, 0);
    const worstCount = entries.reduce((worst, [, n]) => Math.max(worst, n), 0);
    const isCritical = worstCount >= REPORT_ALERT_THRESHOLD;

    const showTooltip = (e: React.MouseEvent<HTMLSpanElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPos({
            top: rect.bottom + 6,
            left: Math.max(8, Math.min(rect.left, window.innerWidth - TOOLTIP_WIDTH - 8)),
        });
    };

    return (
        <>
            <span
                onMouseEnter={showTooltip}
                onMouseLeave={() => setTooltipPos(null)}
                className={`inline-flex flex-shrink-0 cursor-default ${isCritical ? "text-danger" : "text-warning"}`}
                // The colour is not the only carrier of the state: the label spells the count
                // out for screen readers, and the tooltip does it for everyone else.
                aria-label={`${total} signalement${total > 1 ? "s" : ""}`}
            >
                <ExclamationTriangleIcon className="w-4 h-4" />
            </span>

            {/* Portal: the table scrolls inside an overflow container that would clip a
                tooltip anchored in the cell. */}
            {tooltipPos && ReactDOM.createPortal(
                <div
                    style={{ position: "fixed", top: tooltipPos.top, left: tooltipPos.left, zIndex: 9999 }}
                    className="w-56 bg-surface border border-border rounded-xl shadow-float px-3 py-2 pointer-events-none"
                >
                    <div className="space-y-0.5">
                        {entries.map(([type, n]) => (
                            <p key={type} className="text-xs text-ink-muted">
                                <span className={`font-semibold ${n >= REPORT_ALERT_THRESHOLD ? "text-danger" : "text-warning"}`}>
                                    {n} signalement{n > 1 ? "s" : ""}
                                </span>{" "}
                                pour {REPORT_LABELS[type] ?? type}
                            </p>
                        ))}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
